<template>
  <div>
    <UCarousel
      ref="qualityCarousel"
      v-slot="{ item }"
      :items="sectorsItems"
      loop
      fade
      :watch-drag="true"
      :autoplay="{ delay: CAROUSEL_DELAY, stopOnInteraction: false }"
      :watch-resize="true"
      :ui="{
        root: 'flex',
        viewport: 'm-0 min-h-screen',
        container: ['ms-0 size-full'],
        item: [
          'relative',
          'lg:py-0 py-12',
          'flex justify-center items-center',
          'size-full',
          // 防止图片放大时溢出屏幕
          'overflow-hidden',
        ],
        dots: '',
      }"
      @select="updateProgress"
    >
      <!-- 内容层（控制实际高度） -->
      <div
        :class="[
          'page_view',
          'flex flex-col items-start justify-center lg:gap-12 gap-8',
          'w-full',
          'select-none',
        ]"
      >
        <CommonBanner
          :title="item.title"
          :content="item.desc"
          :centered="false"
          theme="dark"
          :has-padding="false"
        />
        <!-- 操作区组件容器 -->
        <div class="flex flex-col lg:gap-10 gap-8 size-full">
          <!-- Tab切换轮播图按钮组 -->
          <div :class="['flex flex-wrap items-center justify-start lg:gap-2.5 gap-3']">
            <!-- 单个Tab按钮 -->
            <div
              v-for="(sectors, index) in sectorsList"
              :key="`quality-tab-btn-${index}`"
              :class="[
                'flex items-center gap-2',
                'lg:w-auto w-full',
                'lg:px-3 px-2 py-2',
                'hover:bg-default/40 hover:cursor-pointer',
                'lg:text-lg text-sm font-semibold uppercase',
                'ring ring-white',
                curCarouselIndex === index ? 'text-secondary bg-default/70' : 'text-white',
              ]"
              @click="jumpTo(index)"
            >
              <UIcon :name="sectors.icon" class="lg:size-6 size-4" />
              <span>
                {{ t(`page.index.quality.content.${sectors.key}.title`) }}
              </span>
            </div>
          </div>

          <!-- 进度条动画 -->
          <div :class="['w-full lg:h-4 h-2', 'bg-default']">
            <div
              :key="curCarouselIndex"
              class="size-full bg_linear_to_r progress_bar"
              :style="{ 'animation-duration': `${CAROUSEL_DELAY}ms` }"
            />
          </div>
        </div>
      </div>

      <!-- 背景层 -->
      <Motion
        as="img"
        :src="item.imgUrl"
        loading="lazy"
        :class="['absolute -z-1 inset-0', 'size-full object-cover', 'brightness-60', 'select-none']"
        :initial="{ scale: 1.1 }"
        :while-in-view="{ scale: 1 }"
        :transition="{
          type: 'spring',
          stiffness: 4,
        }"
      />
    </UCarousel>
  </div>
</template>

<script lang="ts" setup>
  const { t } = useI18n()

  // 单轮播图播放进度
  const curCarouselIndex = ref(0)
  // 轮播图自动切换间隔
  const CAROUSEL_DELAY = 5000

  /**
   * 更新当前轮播图播放进度
   * @param carouselIndex 当前轮播图索引
   */
  const updateProgress = (carouselIndex: number) => {
    curCarouselIndex.value = carouselIndex
  }

  /** 卡片内容 */
  const sectorsList = [
    { key: 'motorcade', icon: 'i-heroicons-truck' },
    { key: 'team', icon: 'i-heroicons-user-group' },
    { key: 'office', icon: 'i-heroicons-building-office' },
    { key: 'warehouse', icon: 'i-heroicons-building-storefront' },
    { key: 'monitor', icon: 'i-heroicons-computer-desktop' },
    { key: 'suspension', icon: 'i-heroicons-shield-check' },
  ]
  const sectorsItems = computed(() =>
    sectorsList.map(({ key, icon }) => ({
      title: t(`page.index.quality.content.${key}.title`),
      desc: t(`page.index.quality.content.${key}.desc`),
      imgUrl: `/img/quality/${key}.webp`,
      icon: icon,
    })),
  )

  const qualityCarousel = useTemplateRef('qualityCarousel')

  function jumpTo(index: number) {
    curCarouselIndex.value = index
    qualityCarousel.value?.emblaApi?.scrollTo(index)
  }
</script>

<style scoped>
  .progress_bar {
    animation: progress linear;
  }
  @keyframes progress {
    from {
      width: 0%;
    }
    to {
      width: 100%;
    }
  }
</style>
